import { openDB } from 'idb'

const DB_NAME = 'routine-app-local-photos'
const DB_VERSION = 2
const STORE = 'local_photos'

let dbPromise = null

function getDb() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion, _newVersion, tx) {
        if (!db.objectStoreNames.contains(STORE)) {
          const store = db.createObjectStore(STORE, { keyPath: 'id' })
          store.createIndex('userId', 'userId', { unique: false })
          store.createIndex('date', 'date', { unique: false })
        }
        if (oldVersion < 2) {
          // v2 adds pendingCloudUpload — a deferred cloud upload waiting for
          // network. Backfill existing records to `false` (they're truly
          // local-only). Add an index so the sync scan is one shot.
          const store = tx.objectStore(STORE)
          if (!store.indexNames.contains('pendingCloudUpload')) {
            store.createIndex('pendingCloudUpload', 'pendingCloudUpload', { unique: false })
          }
          store.openCursor().then(function cursorWalk(cursor) {
            if (!cursor) return
            if (cursor.value.pendingCloudUpload === undefined) {
              cursor.update({ ...cursor.value, pendingCloudUpload: false })
            }
            return cursor.continue().then(cursorWalk)
          })
        }
      },
    })
  }
  return dbPromise
}

export async function addLocalPhoto({ userId, date, pose, caption, blob, pendingCloudUpload = false }) {
  const db = await getDb()
  const record = {
    id: crypto.randomUUID(),
    userId,
    date,
    pose,
    caption: caption || null,
    blob,
    pendingCloudUpload: !!pendingCloudUpload,
    createdAt: Date.now(),
  }
  await db.put(STORE, record)
  return record
}

export async function getLocalPhotos(userId) {
  if (!userId) return []
  const db = await getDb()
  const all = await db.getAllFromIndex(STORE, 'userId', userId)
  return all
}

// Returns photos waiting to sync to cloud. IndexedDB indexes can't be
// queried by boolean directly, so we scan-filter — fine since the pending
// set is small (anything pending will be drained within seconds of going
// online).
export async function getPendingCloudPhotos(userId) {
  const all = userId ? await getLocalPhotos(userId) : []
  if (userId) return all.filter((p) => p.pendingCloudUpload)
  const db = await getDb()
  const everything = await db.getAll(STORE)
  return everything.filter((p) => p.pendingCloudUpload)
}

export async function deleteLocalPhoto(id) {
  const db = await getDb()
  await db.delete(STORE, id)
}

export async function clearLocalPhotos(userId) {
  const db = await getDb()
  if (userId) {
    const tx = db.transaction(STORE, 'readwrite')
    const all = await tx.store.index('userId').getAll(userId)
    for (const r of all) await tx.store.delete(r.id)
    await tx.done
  } else {
    await db.clear(STORE)
  }
}

export async function estimateLocalStorage() {
  if (typeof navigator !== 'undefined' && navigator.storage?.estimate) {
    try {
      return await navigator.storage.estimate()
    } catch {
      return null
    }
  }
  return null
}
